import React, { useEffect, useState, useCallback } from "react";
import ContentShell from "components/ContentShell";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  Divider,
  IconButton,
  Alert,
  Paper,
  Chip,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import { SimpleTextInput } from "components/FormTextInput";
import { SimpleSelectInput } from "components/FormSelectInput";
import {
  createVaultRewardConfig,
  updateVaultRewardConfig,
  getVaultRewardConfigInfo,
} from "services/vaultRewardConfigService";
import { getBoosters } from "services/packServices";
import { vaultRewardConfigValidation } from "utils/validation";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SearchableInput from "components/SearchableInput";
import { getUniquesArrayObject } from "utils/miscUtils";
import debounce from "lodash.debounce";

const useStyles = makeStyles((theme) => ({
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: theme.spacing(2),
  },
  headerSection: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(3),
    backgroundColor: "#fafafa",
    borderRadius: "12px",
    border: "1px solid #e0e0e0",
  },
  infoBox: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: "#e3f2fd",
    borderRadius: "8px",
    border: "1px solid #90caf9",
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  rewardCard: {
    marginBottom: theme.spacing(2),
    border: "2px solid #e0e0e0",
    borderRadius: "12px",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    },
  },
  rewardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 3),
    backgroundColor: "#f8f9fa",
    borderBottom: "1px solid #e0e0e0",
    borderRadius: "10px 10px 0 0",
  },
  rewardContent: {
    padding: theme.spacing(3),
  },
  slotTypeChip: {
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  addButton: {
    margin: theme.spacing(2, 0),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(2, 3),
    borderRadius: "8px",
    borderStyle: "dashed",
    borderWidth: "2px",
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  progressSection: {
    padding: theme.spacing(3),
    margin: theme.spacing(2, 0),
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    border: "2px solid #e0e0e0",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  },
  progressBar: {
    height: "12px",
    borderRadius: "6px",
    marginTop: theme.spacing(1),
  },
  progressText: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  buttonContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
    padding: theme.spacing(2, 0),
    justifyContent: "flex-end",
  },
  actionButton: {
    padding: theme.spacing(1.5, 4),
    borderRadius: "8px",
    textTransform: "none",
    fontSize: "1rem",
    fontWeight: "600",
  },
  rewardSummary: {
    padding: theme.spacing(2),
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
    marginTop: theme.spacing(2),
  },
  validationMessage: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: "4px",
    marginTop: theme.spacing(1),
  },
  duplicateButton: {
    marginLeft: theme.spacing(1),
    minWidth: "auto",
    padding: theme.spacing(0.5),
  },
}));

const SLOT_TYPES = [
  { id: "Player", label: "Player" },
  { id: "Coins", label: "Coins" },
  { id: "Booster", label: "Booster" },
];

const INITIAL_REWARD = {
  probability: 0,
  slotType: "Player",
  playerOvr: null,
  coins: null,
  booster: null,
};

const VaultRewardConfigEditor = () => {
  const styles = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const id = location.pathname.split("/").slice(-1)[0];
  const isCreateMode = id === "create";
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [level, setLevel] = useState("");
  const [rewards, setRewards] = useState([{ ...INITIAL_REWARD }]);
  const [boosterOptions, setBoosterOptions] = useState([]);
  const [searchedBoosters, setSearchedBoosters] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Helper functions
  const getSlotTypeColor = (slotType: any) => {
    const colors = {
      Player: "#4caf50",
      Coins: "#ff9800",
      Booster: "#2196f3",
    };
    return colors[slotType] || "#9e9e9e";
  };

  const getSlotTypeIcon = (slotType: any) => {
    const icons = {
      Player: "👤",
      Coins: "🪙",
      Booster: "🚀",
    };
    return icons[slotType] || "❓";
  };

  const getBoosterDisplayName = (booster: any) => {
    if (!booster) return "Not selected";

    // Handle different booster object structures
    if (typeof booster === "string") return booster;
    if (typeof booster === "object") {
      return (
        booster.label ||
        booster.name ||
        booster.displayName ||
        booster.id ||
        "Unknown booster"
      );
    }

    return "Unknown booster";
  };

  const duplicateReward = (index: any) => {
    if (rewards.length >= 12) {
      showSnackbar("Maximum 12 rewards allowed", "error");
      return;
    }
    const rewardToDuplicate = { ...rewards[index] };
    const newRewards = [...rewards];
    newRewards.splice(index + 1, 0, rewardToDuplicate);
    setRewards(newRewards);
    setHasUnsavedChanges(true);
  };

  const validateReward = (reward, index) => {
    const errors = [];

    if (!reward.probability || reward.probability <= 0) {
      errors.push("Probability must be greater than 0");
    }

    if (reward.probability > 100) {
      errors.push("Probability cannot exceed 100%");
    }

    if (
      reward.slotType === "Player" &&
      (!reward.playerOvr || reward.playerOvr < 50 || reward.playerOvr > 99)
    ) {
      errors.push("Player OVR must be between 50 and 99");
    }

    if (reward.slotType === "Coins" && (!reward.coins || reward.coins < 1)) {
      errors.push("Coins amount must be greater than 0");
    }

    if (
      reward.slotType === "Booster" &&
      (!reward.booster ||
        (typeof reward.booster === "object" &&
          !reward.booster.id &&
          !reward.booster.label))
    ) {
      errors.push("Booster must be selected");
    }

    return errors;
  };

  const cancel = useCallback(
    () =>
      navigate(
        configData.routes.vaultRewardsAndDropRates.vaultRewardsAndDropRates
      ),
    [navigate]
  );

  const fetchBoosters = async (searchValue = "") => {
    try {
      const response = await getBoosters();
      const boostersList = response?.data || [];

      const options = boostersList
        .filter(
          (booster) =>
            !searchValue ||
            booster.displayName
              ?.toLowerCase()
              .includes(searchValue.toLowerCase())
        )
        .map((booster) => ({
          id: booster._id,
          label: booster.displayName || booster.name,
        }));

      if (searchValue) {
        setSearchedBoosters(
          getUniquesArrayObject([...options, ...searchedBoosters])
        );
      } else {
        setBoosterOptions(options);
        setSearchedBoosters(options);
      }
    } catch (error) {
      showSnackbar(error.message, "error");
    }
  };

  const handleBoosterSearch = debounce(async (searchValue) => {
    if (searchValue) {
      await fetchBoosters(searchValue);
    } else {
      setSearchedBoosters(boosterOptions);
    }
  }, 300);

  const fetchVaultRewardConfigInfo = async () => {
    try {
      setLoading(true);
      const response = await getVaultRewardConfigInfo(id);
      setLevel(response.data.level?.toString() || "");

      // Process rewards to convert booster IDs to proper booster objects
      const processedRewards = await Promise.all(
        (response.data.rewards || []).map(async (reward) => {
          if (reward.slotType === "Booster" && reward.booster) {
            try {
              // If booster is already an object with label, use it as is
              if (typeof reward.booster === "object" && reward.booster.label) {
                return reward;
              }

              // If booster is an ID, fetch the booster details
              const boosterId =
                typeof reward.booster === "object"
                  ? reward.booster.id
                  : reward.booster;
              const boosterResponse = await getBoosters(``, 1, 1000);
              const foundBooster = boosterResponse.data.find(
                (b) => b._id === boosterId
              );

              if (foundBooster) {
                return {
                  ...reward,
                  booster: {
                    id: foundBooster._id,
                    label: foundBooster.displayName || foundBooster.name,
                  },
                };
              }
            } catch (error) {
              console.error("Error fetching booster details:", error);
            }
          }
          return reward;
        })
      );

      setRewards(processedRewards);
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const addReward = () => {
    if (rewards.length >= 12) {
      showSnackbar("Maximum 12 rewards allowed", "error");
      return;
    }
    setRewards([...rewards, { ...INITIAL_REWARD }]);
    setHasUnsavedChanges(true);
  };

  const removeReward = (index: any) => {
    if (rewards.length === 1) {
      showSnackbar("At least one reward is required", "error");
      return;
    }
    const newRewards = rewards.filter((_, i) => i !== index);
    setRewards(newRewards);
    setHasUnsavedChanges(true);
  };

  const updateReward = (index, field, value) => {
    console.log(`Updating reward ${index}, field: ${field}, value:`, value);
    const newRewards = [...rewards];

    // Reset other fields when slot type changes
    if (field === "slotType") {
      newRewards[index] = {
        ...INITIAL_REWARD,
        probability: newRewards[index].probability,
        slotType: value,
      };
    } else {
      newRewards[index] = {
        ...newRewards[index],
        [field]: value,
      };
    }

    setRewards(newRewards);
    setHasUnsavedChanges(true);
  };

  const calculateTotalProbability = () => {
    return rewards.reduce(
      (sum, reward) => sum + (Number(reward.probability) || 0),
      0
    );
  };

  const validateForm = async () => {
    try {
      // Prepare validation data without modifying the current state
      const validationData = {
        level: parseInt(level),
        rewards: rewards.map((reward) => ({
          ...reward,
          probability: Number(reward.probability),
          playerOvr: reward.playerOvr ? Number(reward.playerOvr) : null,
          coins: reward.coins ? Number(reward.coins) : null,
          // For validation, convert booster object to ID if needed
          booster:
            reward.slotType === "Booster" && reward.booster
              ? typeof reward.booster === "object"
                ? reward.booster.id
                : reward.booster
              : reward.booster,
        })),
      };

      await vaultRewardConfigValidation.validate(validationData);
      return true;
    } catch (error) {
      showSnackbar(error.message, "error");
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!(await validateForm())) return;

    try {
      setLoading(true);

      const payload = {
        level: parseInt(level),
        rewards: rewards.map((reward) => ({
          probability: Number(reward.probability),
          slotType: reward.slotType,
          ...(reward.slotType === "Player" && {
            playerOvr: Number(reward.playerOvr),
          }),
          ...(reward.slotType === "Coins" && { coins: Number(reward.coins) }),
          ...(reward.slotType === "Booster" && {
            booster:
              typeof reward.booster === "object"
                ? reward.booster.id
                : reward.booster,
          }),
        })),
      };

      if (id !== "create") {
        (payload as any)._id = id;
      }

      const response =
        id === "create"
          ? await createVaultRewardConfig(payload)
          : await updateVaultRewardConfig(payload);

      showSnackbar(response.message || "Saved successfully", "success");
      setHasUnsavedChanges(false);
      cancel();
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const renderRewardFields = (reward, index) => {
    const errors = validateReward(reward, index);
    const hasErrors = errors.length > 0;

    return (
      <Box>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={3}>
            <SimpleTextInput
              label="Probability (%)"
              name="probability"
              type="number"
              value={reward.probability}
              onChange={(e) =>
                updateReward(index, "probability", e.target.value)
              }
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              error={
                hasErrors &&
                (!reward.probability ||
                  reward.probability <= 0 ||
                  reward.probability > 100)
              }
              helperText={
                hasErrors && (!reward.probability || reward.probability <= 0)
                  ? "Required: 0-100%"
                  : reward.probability > 100
                  ? "Cannot exceed 100%"
                  : `${reward.probability}% of total probability`
              }
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <SimpleSelectInput
              label="Slot Type"
              name="slotType"
              value={reward.slotType}
              onChange={(e) => updateReward(index, "slotType", e.target.value)}
              options={SLOT_TYPES}
            />
          </Grid>

          {reward.slotType === "Player" && (
            <Grid item xs={12} sm={3}>
              <SimpleTextInput
                label="Player OVR"
                name="playerOvr"
                type="number"
                value={reward.playerOvr || ""}
                onChange={(e) =>
                  updateReward(index, "playerOvr", e.target.value)
                }
                inputProps={{ min: 50, max: 99 }}
                error={
                  hasErrors &&
                  (!reward.playerOvr ||
                    reward.playerOvr < 50 ||
                    reward.playerOvr > 99)
                }
                helperText="Required: 50-99 OVR"
              />
            </Grid>
          )}

          {reward.slotType === "Coins" && (
            <Grid item xs={12} sm={3}>
              <SimpleTextInput
                label="Coins Amount"
                name="coins"
                type="number"
                value={reward.coins || ""}
                onChange={(e) => updateReward(index, "coins", e.target.value)}
                inputProps={{ min: 1 }}
                error={hasErrors && (!reward.coins || reward.coins < 1)}
                helperText="Required: Minimum 1 coin"
              />
            </Grid>
          )}

          {reward.slotType === "Booster" && (
            <Grid item xs={12} sm={3}>
              <SearchableInput
                label="Booster"
                name="booster"
                options={searchedBoosters}
                selected={reward.booster}
                onInputChange={handleBoosterSearch}
                onOptionSelect={(event) => {
                  console.log("Selected booster event:", event);
                  // SearchableInput returns { target: { name, value } } structure
                  const boosterValue = event.target
                    ? event.target.value
                    : event;
                  console.log("Extracted booster value:", boosterValue);
                  updateReward(index, "booster", boosterValue);
                }}
                placeholder="Select a booster"
                error={
                  hasErrors &&
                  (!reward.booster ||
                    (typeof reward.booster === "object" &&
                      !reward.booster.id &&
                      !reward.booster.label))
                }
                helperText={
                  reward.booster
                    ? `Selected: ${getBoosterDisplayName(reward.booster)}`
                    : "Required: Select a booster"
                }
              />
            </Grid>
          )}

          <Grid item xs={12} sm={3}>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                height: "100%",
              }}
            >
              <Tooltip title="Duplicate this reward">
                <IconButton
                  size="small"
                  onClick={() => duplicateReward(index)}
                  className={styles.duplicateButton}
                  disabled={rewards.length >= 12}
                >
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>

        {/* Reward Summary */}
        <Box className={styles.rewardSummary}>
          <Typography variant="body2" color="textSecondary">
            <strong>Summary:</strong> {getSlotTypeIcon(reward.slotType)}{" "}
            {reward.probability}% chance for {reward.slotType}
            {reward.slotType === "Player" &&
              reward.playerOvr &&
              ` (OVR ${reward.playerOvr})`}
            {reward.slotType === "Coins" &&
              reward.coins &&
              ` (${reward.coins.toLocaleString()} coins)`}
            {reward.slotType === "Booster" &&
              reward.booster &&
              ` (${getBoosterDisplayName(reward.booster)})`}
          </Typography>
        </Box>

        {/* Validation Errors */}
        {hasErrors && (
          <Alert severity="error" sx={{ mt: 1 }}>
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {errors.map((error, errorIndex) => (
                <li key={errorIndex}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}
      </Box>
    );
  };

  useEffect(() => {
    const loadData = async () => {
      // Always fetch boosters first
      await fetchBoosters();

      // Then fetch vault config info if editing
      if (id !== "create") {
        await fetchVaultRewardConfigInfo();
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const totalProbability = calculateTotalProbability();

  return (
    <ContentShell
      title={`${
        isCreateMode ? "Create New" : "Edit"
      } Vault Reward Configuration`}
      loading={loading}
      showBackButton
      onBackButtonClick={cancel}
    >
      <Box className={styles.container}>
        {/* Header Information Section */}
        <Paper className={styles.headerSection} elevation={2}>
          <Typography variant="h5" gutterBottom color="primary">
            {isCreateMode
              ? "🆕 Create New Configuration"
              : "✏️ Edit Configuration"}
          </Typography>

          <Box className={styles.infoBox}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <InfoIcon sx={{ fontSize: 16, mr: 1, verticalAlign: "middle" }} />
              Configure vault rewards for a specific level. Each reward has a
              probability percentage that determines drop rates.
            </Typography>
            <Typography variant="body2">
              <strong>Rules:</strong> Total probability must equal 100% •
              Maximum 12 rewards
            </Typography>
          </Box>

          <Box className={styles.field}>
            <SimpleTextInput
              label="Vault Level"
              name="level"
              type="number"
              value={level}
              onChange={(e) => {
                setLevel(e.target.value);
                setHasUnsavedChanges(true);
              }}
              inputProps={{ min: 1 }}
              required
              helperText="The vault level this configuration applies to"
            />
          </Box>
        </Paper>

        {/* Rewards Configuration Section */}
        <Typography
          variant="h6"
          gutterBottom
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          🎁 Rewards Configuration
          <Chip
            label={`${rewards.length}/12`}
            size="small"
            color={rewards.length >= 12 ? "error" : "primary"}
          />
        </Typography>

        {rewards.map((reward, index) => (
          <Card key={index} className={styles.rewardCard} elevation={3}>
            <Box className={styles.rewardHeader}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                  {getSlotTypeIcon(reward.slotType)} Reward {index + 1}
                </Typography>
                <Chip
                  label={reward.slotType}
                  size="small"
                  className={styles.slotTypeChip}
                  sx={{
                    backgroundColor: getSlotTypeColor(reward.slotType),
                    color: "white",
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                {rewards.length > 1 && (
                  <Tooltip title="Remove this reward">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeReward(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
            <CardContent className={styles.rewardContent}>
              {renderRewardFields(reward, index)}
            </CardContent>
          </Card>
        ))}

        {/* Add Reward Button */}
        {rewards.length < 12 && (
          <Button
            variant="outlined"
            onClick={addReward}
            className={styles.addButton}
            startIcon={<AddIcon />}
            fullWidth
            size="large"
          >
            Add New Reward ({rewards.length}/12)
          </Button>
        )}

        {/* Progress and Validation Section */}
        <Paper className={styles.progressSection} elevation={2}>
          <Box className={styles.progressText}>
            <Typography variant="h6">Probability Distribution</Typography>
            <Typography
              variant="h6"
              color={totalProbability === 100 ? "success.main" : "warning.main"}
            >
              {totalProbability.toFixed(2)}% / 100%
            </Typography>
          </Box>

          <LinearProgress
            variant="determinate"
            value={Math.min(totalProbability, 100)}
            className={styles.progressBar}
            color={
              totalProbability === 100
                ? "success"
                : totalProbability > 100
                ? "error"
                : "warning"
            }
          />

          <Box className={styles.validationMessage}>
            {totalProbability === 100 ? (
              <>
                <CheckCircleIcon color="success" />
                <Typography color="success.main">
                  Perfect! Total probability equals 100%
                </Typography>
              </>
            ) : totalProbability > 100 ? (
              <>
                <WarningIcon color="error" />
                <Typography color="error.main">
                  Total probability exceeds 100% by{" "}
                  {(totalProbability - 100).toFixed(2)}%
                </Typography>
              </>
            ) : (
              <>
                <WarningIcon color="warning" />
                <Typography color="warning.main">
                  Total probability is {(100 - totalProbability).toFixed(2)}%
                  below 100%
                </Typography>
              </>
            )}
          </Box>

          {/* Rewards Summary */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Rewards Summary:
            </Typography>
            {rewards.map((reward, index) => (
              <Typography key={index} variant="body2" sx={{ ml: 2 }}>
                • {reward.probability}% - {getSlotTypeIcon(reward.slotType)}{" "}
                {reward.slotType}
                {reward.slotType === "Player" &&
                  reward.playerOvr &&
                  ` (OVR ${reward.playerOvr})`}
                {reward.slotType === "Coins" &&
                  reward.coins &&
                  ` (${Number(reward.coins).toLocaleString()} coins)`}
                {reward.slotType === "Booster" &&
                  reward.booster &&
                  ` (${getBoosterDisplayName(reward.booster)})`}
              </Typography>
            ))}
          </Box>
        </Paper>

        {/* Action Buttons */}
        <Divider sx={{ my: 3 }} />

        <Box className={styles.buttonContainer}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={cancel}
            className={styles.actionButton}
            size="large"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={loading || totalProbability !== 100}
            className={styles.actionButton}
            size="large"
          >
            {loading
              ? "Saving..."
              : isCreateMode
              ? "Create Configuration"
              : "Update Configuration"}
          </Button>
        </Box>

        {hasUnsavedChanges && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              You have unsaved changes. Make sure to save your configuration
              before leaving.
            </Typography>
          </Alert>
        )}
      </Box>
    </ContentShell>
  );
};

export default VaultRewardConfigEditor;
